import '../../../gettext'
import defaultConfig from '../../default'
import type { GatewayConfig } from '../../declarations'
import HeaderLogo from './HeaderLogo.svelte'

export default {
  ...defaultConfig,

  gateway: 'yale',
  title: window.gettext('Yale Framework'),
  logoComponent: HeaderLogo,
  pages: {
    ...defaultConfig.pages,
    home: () => import('./Home.svelte'),
  },
  footer: [
    {
      label: window.gettext('About'),
      href: '/about/',
    },
    {
      label: window.gettext('Terms of Use'),
      href: '/pages/terms-service/',
    },
    {
      label: window.gettext('Support Services'),
      href: '/services/services/',
    },
    {
      label: window.gettext('Contact Us'),
      href: '/services/contact/',
    },
  ],
  navigation: user => {
    const navigation = defaultConfig.navigation(user)

    return [
      {
        id: 'get-started',
        label: window.gettext('Get Started'),
        columns: [
          [
            {
              type: 'heading',
              label: window.gettext('Before You Start'),
            },
            {
              type: 'link',
              label: window.gettext('Framework Recommendations'),
              href: '/pages/frameworkRecommendations/',
            },
            {
              type: 'link',
              label: window.gettext('Coping with Uncertainty'),
              href: '/pages/coping-uncertainty/',
            },
            {
              type: 'link',
              label: window.gettext('Take a Data Basin Tour'),
              href: '/tour/',
            },
            { type: 'divider' },
            {
              type: 'link',
              label: window.gettext('Help'),
              href: '/help/',
              bold: true,
            },
            {
              type: 'link',
              label: window.gettext('Glossary'),
              href: '/glossary/',
              bold: true,
            },
          ],
          [
            {
              type: 'heading',
              label: window.gettext('About the Framework'),
            },
            {
              type: 'link',
              label: window.gettext('About Data Basin'),
              href: '/about/',
            },
            {
              type: 'link',
              label: window.gettext('Supporting Documents'),
              href: '/pages/documents/',
            },
            {
              type: 'link',
              label: window.gettext('Framework FAQ'),
              href: '/pages/faq/',
            },
          ],
          [
            {
              type: 'heading',
              label: window.gettext('Using the Framework'),
            },
            {
              type: 'link',
              label: window.gettext('The Framework Matrix'),
              href: '/pages/matrix/',
            },
            {
              type: 'link',
              label: window.gettext('Choosing adaptation objectives'),
              href: '/pages/choosingStrategies/',
            },
            {
              type: 'link',
              label: window.gettext('Choosing ecological analysis levels'),
              href: '/pages/choosingLevels/',
            },
          ],
        ],
      },

      {
        id: 'explore',
        label: window.gettext('Explore'),
        columns: [
          [
            {
              type: 'heading',
              label: window.gettext('Pilot Projects'),
            },
            {
              type: 'link',
              label: window.gettext('Overview'),
              href: '/pilots/',
            },
            {
              type: 'link',
              label: window.gettext('Conservation Biology Institute'),
              href: '/articles/ab30695f87554c6aab9b269630c84551/',
            },
            {
              type: 'link',
              label: window.gettext('EcoAdapt'),
              href: '/articles/d37e178136a04be9be7ba256ada69cf6/',
            },
            {
              type: 'link',
              label: window.gettext('Florida Natural Areas Inventory'),
              href: '/articles/b6ddc495c20b4b9aa05cb2925cebe1bf/',
            },
            {
              type: 'link',
              label: window.gettext('Geos Institute'),
              href: '/articles/172d089c062b4fb686cf18565df7dc57/',
            },
            {
              type: 'link',
              label: window.gettext('NatureServe'),
              href: '/articles/b03c8f2329e644dd9a9bb0efc23cb17e/',
            },
            {
              type: 'link',
              label: window.gettext('University of Washington'),
              href: '/articles/7d6cc2da6b1d49e7b7db55590eff0c70/',
            },
          ],
          [
            {
              type: 'heading',
              label: window.gettext('Adaptation Objectives'),
            },
            {
              type: 'link',
              label: window.gettext('1. Protect current patterns of biodiversity'),
              href: '/objectives_1/',
            },
            {
              type: 'link',
              label: window.gettext('2. Protect large, intact, natural landscapes and ecological processes'),
              href: '/objectives_3/',
            },
            {
              type: 'link',
              label: window.gettext('3. Protect the geophysical setting'),
              href: '/objectives_6/',
            },
            {
              type: 'link',
              label: window.gettext('4. Identify and manage areas that will provide future climate space'),
              href: '/objectives_2/',
            },
            {
              type: 'link',
              label: window.gettext('5. Identify and protect climate refugia'),
              href: '/objectives_5/',
            },
            {
              type: 'link',
              label: window.gettext('6. Maintain and restore ecological connectivity'),
              href: '/objectives_4/',
            },
          ],
          [
            {
              type: 'heading',
              label: window.gettext('Approaches & Tools'),
            },
            {
              type: 'link',
              label: window.gettext('Conducting Assessments of Species Distributions'),
              href: '/speciesDistributions/',
            },
            {
              type: 'link',
              label: window.gettext('Conducting Assessments of Species Landscape Movement'),
              href: '/landscapeMovement/',
            },
          ],
          [
            {
              type: 'heading',
              label: window.gettext('Data by topic'),
            },
            {
              type: 'link',
              label: window.gettext('Abiotic Data'),
              href: '/pages/abiotic/',
            },
            {
              type: 'link',
              label: window.gettext('Biological Data'),
              href: '/pages/biological/',
            },
            {
              type: 'link',
              label: window.gettext('Cultural Data'),
              href: '/pages/cultural/',
            },
            { type: 'divider' },
            {
              type: 'heading',
              label: window.gettext('Data by type'),
            },
            {
              type: 'link',
              label: window.gettext('Galleries'),
              href: '/galleries/',
            },
            {
              type: 'link',
              label: window.gettext('Maps'),
              href: '/maps/',
            },
            {
              type: 'link',
              label: window.gettext('Datasets'),
              href: '/datasets/',
            },
          ],
        ],
      },

      navigation.find(menu => menu.id === 'create'),

      {
        id: 'community',
        label: window.gettext('Community'),
        width: '215px',
        items: [
          {
            type: 'link',
            label: window.gettext('Framework Groups'),
            href: '/groups/',
          },
          {
            type: 'link',
            label: window.gettext('Data Basin Members'),
            href: '/people/',
          },
          {
            type: 'link',
            label: window.gettext('The Science Panel'),
            href: '/pages/panelmembers',
          },
          {
            type: 'link',
            label: window.gettext('The Data Basin Team'),
            href: '/people/databasin-team/',
          },
        ],
      },
    ]
  },
} as GatewayConfig
